import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
// eslint-disable-next-line import/no-cycle
import ShippingProtectionPage from '../app/containers/ShippingProtectionPage';
import { createEmbeddedPageRequest } from '../common/apiRequest';
import { postData } from '../common/api';
import { getShop, isShopifyThemeEditor } from '../common/pageData';
import { getOfferEndpoint } from '../common/constants';
import { createEmebeddedContainerWrapper } from '../common/html';
import { GlobalStyle } from '../app/components/skins';
// eslint-disable-next-line import/no-cycle
import Init from '../app/containers/Init';
import { setOfferViewed } from '../common/offer';
import { getMerchantSpecificThemeFlags } from '../app/components/helpers/offer';
import {
  getShipProOfferID,
  getShipProAutoAdd,
  setShipProOfferID,
  setShipProVariants,
  getShipProVariants,
} from '../common/settings';
import {
  updateCart,
  removeShipProAndModifyCartIfNotEnabled,
  getMRCartFromShopifyCart,
  fetchCart,
  addOfferedVariantsToCart,
  getMonkDiscountProperties,
} from '../common/cart';
import { logger } from '../common/logger';
import { StaticMutexKeys, mutexManager } from '../common/mutexmanager';
import { goToCart } from '../common/navigation';
import {
  logRuntimeError,
  logRuntimeInfo,
  logtailMiddleware,
} from '../common/utils';

export const checkShippingProtectionAndDisplay = async ({
  cartPageDiscountConfig,
  isDiscountCodeEnabled,
  apiCart,
  widgets,
}) => {
  if (!widgets) {
    return;
  }

  let widget = null;
  for (let i = 0; i < widgets.length; i += 1) {
    if (widgets[i].offerType === 'ship_pro') {
      widget = widgets[i];
      break;
    }
  }

  if (!widget) {
    return;
  }

  const widgetMutex = mutexManager.getMutex(StaticMutexKeys.SHIP_PRO);
  await widgetMutex.acquire();
  try {
    const { mrWidgetID, sections } = widget;

    const source = 'cart_page';
    const pageReq = await createEmbeddedPageRequest({
      source,
      offerType: 'ship_pro',
      apiCart,
      widget,
    });

    let { cart: mrCart } = pageReq;

    const storedShipProVariants = getShipProVariants();
    let isShipProVariantAlreadyInCart = false;
    let existingShipProVariantID = 0;
    if (storedShipProVariants && storedShipProVariants.length > 0) {
      for (let i = 0; i < mrCart.length; i += 1) {
        const { sp_variant_id } = mrCart[i];
        if (storedShipProVariants.includes(sp_variant_id)) {
          isShipProVariantAlreadyInCart = true;
          existingShipProVariantID = sp_variant_id;
          break;
        }
      }
    }

    const response = await postData(getOfferEndpoint(), pageReq);

    if (!response) {
      return;
    }

    const { offer, is_eligible } = response;
    const {
      offered: products,
      mr_offer_id,
      shipProOfferedVariant,
      theme,
    } = offer;

    logtailMiddleware({
      widgetId: widget.id,
      basicOfferData: {
        offered: products,
      },
      offerName: 'shipPro',
    });

    // if the current offer is different from an existing offer then remove the variants
    // from previous offer
    const storedOfferId = getShipProOfferID();
    if (is_eligible && storedOfferId !== mr_offer_id) {
      const cartChanged = await removeShipProAndModifyCartIfNotEnabled({
        mrCart,
        isRefresh: theme.isRefreshPageAfterProductAddition,
      });
      // fetch new cart on change
      if (cartChanged) {
        const cart = fetchCart();
        mrCart = getMRCartFromShopifyCart(cart);
      }
    }

    // in case the offer is eligible store the data so that it can be used later
    if (is_eligible) {
      const product = products[0];
      const { variants } = product;
      const variantIDs = variants.reduce(
        (acc, curr) => acc.concat(curr.id),
        [],
      );
      setShipProOfferID(mr_offer_id);
      setShipProVariants(JSON.stringify(variantIDs));

      // if the user had already set shipping protection to true, then if the variant changes, we should still
      // add the new variant to cart
      if (
        isShipProVariantAlreadyInCart &&
        existingShipProVariantID !== shipProOfferedVariant.id
      ) {
        const cartChanged = await onShipProToggle({
          isOn: true,
          selectedVariant: shipProOfferedVariant,
          variants,
          cartDiscountText: theme.cartDiscountText,
          isRefresh: theme.isRefreshPageAfterProductAddition,
        });
        if (cartChanged) {
          const cart = await fetchCart();
          mrCart = getMRCartFromShopifyCart(cart);
        }
      }

      // it is possible that the user added multiple quantities, so restrict the quantity to only one
      const cartChanged = await restrictShipProQuantities({
        mrCart,
        selectedVariant: shipProOfferedVariant,
        variantIDs,
        isRefresh: theme.isRefreshPageAfterProductAddition,
      });
      if (cartChanged) {
        const cart = await fetchCart();
        mrCart = getMRCartFromShopifyCart(cart);
      }
    }

    const isAutoAddedOnce = getShipProAutoAdd();
    // display / hide the widget
    const displayAfterMs = 0;
    setTimeout(() => {
      displayShippingProtection({
        is_eligible,
        isAutoAddedOnce,
        mrCart,
        offer,
        cartPageDiscountConfig,
        isDiscountCodeEnabled,
        mrWidgetID,
        sections,
        onToggle: onShipProToggle,
      });
    }, displayAfterMs);

    // if offer is not eligible then remove any variant present from an existing offer
    if (!is_eligible) {
      await removeShipProAndModifyCartIfNotEnabled({ mrCart });
      return;
    }
  } catch (e) {
    logRuntimeError({
      errorMessage: `${e.name} ${e.message}`,
      errorData: {
        fileName: 'shipProModule',
        level: 'error',
        functionName: 'checkShippingProtectionAndDisplay',
      },
      errorStack: e,
    });
    logger.error(e);
  } finally {
    widgetMutex.release();
  }
};

const displayShippingProtection = async ({
  is_eligible,
  isAutoAddedOnce,
  mrCart,
  offer,
  cartPageDiscountConfig,
  isDiscountCodeEnabled,
  mrWidgetID,
  sections,
  onToggle,
}) => {
  try {
    const containers = [];
    for (let i = 0; i < sections.length; i += 1) {
      const section = sections[i];
      const { selector, position, sectionType } = section;

      containers.push({
        selector,
        position,
        containerID: getShippingProtectionContainerID({
          sectionType,
          selector,
          mrWidgetID,
        }),
        isDrawer: sectionType === 'cart_drawer',
      });
    }

    // if offer is ineligble then hide the widget
    if (!is_eligible) {
      for (let i = 0; i < containers.length; i += 1) {
        const { containerID } = containers[i];
        renderShippingProtectionInDiv({ close: true }, containerID);
      }
      return;
    }

    const tasks = [];
    for (let i = 0; i < containers.length; i += 1) {
      const { containerID, selector, position, isDrawer } = containers[i];
      tasks.push(
        createContainerAndRender({
          isAutoAddedOnce,
          mrCart,
          offer,
          onToggle,
          containerID,
          isDrawer,
          selector,
          position,
        }),
      );
    }

    const result = await Promise.all(tasks);
    const rendered = result.reduce((acc, curr) => acc || curr.value, false);
    const { mr_offer_id } = offer;

    if (rendered) {
      setOfferViewed({
        mr_offer_id,
        checkDiscount: false,
        cartPageDiscountConfig,
        isDiscountCodeEnabled,
      });
    }
  } catch (e) {
    logRuntimeError({
      errorMessage: `${e.name} ${e.message}`,
      errorData: {
        fileName: 'shipProModule',
        level: 'error',
        functionName: 'displayShippingProtection',
      },
      errorStack: e,
    });
  }
};

const createContainerAndRender = async ({
  isAutoAddedOnce,
  onToggle,
  offer,
  mrCart,
  containerID,
  selector,
  position,
}) => {
  const existing = document.getElementById(containerID);
  if (existing) {
    renderShippingProtectionInDiv(
      { offer, mrCart, isAutoAddedOnce, onToggle },
      containerID,
    );
    return true;
  }

  const selectors = selector.split(',');
  const positions = position.split(',');
  let container;
  for (let i = 0; i < selectors.length; i += 1) {
    // eslint-disable-next-line no-await-in-loop
    container = await createEmebeddedContainerWrapper({
      id: containerID,
      selector: selectors[i].trim(),
      position: positions[i],
      retryContainerCreation: true,
    });

    if (container) {
      break;
    }
  }

  if (container) {
    renderShippingProtectionInDiv(
      { offer, mrCart, isAutoAddedOnce, onToggle },
      containerID,
    );
    return true;
  }
  return false;
};

const getShippingProtectionContainerID = ({ sectionType, mrWidgetID }) => {
  // cannot change the id since it is predefined and exists in old stores.
  const trigger = 'cp';
  const offer = 'ship_pro';
  let section = sectionType;

  switch (sectionType) {
    case 'cart_page':
      section = 'page';
      break;

    case 'cart_drawer':
      section = 'drawer';
      break;

    default:
      break;
  }

  let id = `mr-div-embedded-${trigger}-${offer}-${section}`;
  if (mrWidgetID) {
    id = `${id}-${mrWidgetID}`;
  }
  return id;
};

let store = null;
export const setStore = st => {
  store = st;
};

export function renderShippingProtectionInDiv(
  { offer, onToggle, isAutoAddedOnce, mrCart, close },
  id,
) {
  const container = document.getElementById(id);

  if (!container) {
    return;
  }

  logger.log('render ship pro');
  if (close && isShopifyThemeEditor()) {
    // for shopify theme editor leave the div as it is
    // TODO: handle case where the offer is displayed and then removed
    // so that dummy values can be displayed
    return;
  }

  if (!close) {
    offer.theme = getMerchantSpecificThemeFlags({
      theme: offer.theme,
      shopURL: getShop(),
      offerType: 'ship_pro',
    });
  }

  ReactDOM.render(
    <Provider store={store}>
      <div>
        <GlobalStyle />
        <Init />
        {!close && (
          <ShippingProtectionPage
            offer={offer}
            isAutoAddedOnce={isAutoAddedOnce}
            onToggle={onToggle}
            mrCart={mrCart}
          />
        )}
      </div>
    </Provider>,
    container,
  );
}

export async function onShipProToggle({
  isOn,
  selectedVariant,
  variants,
  cartDiscountText,
  isRefresh,
}) {
  let changed = false;
  if (isOn) {
    changed = await addShipProVariant({
      selectedVariant,
      variants,
      cartDiscountText,
    });
  } else {
    changed = await removeShipProVariant({ variants });
  }
  if (changed && isRefresh) {
    goToCart({ isPopup: false, isRefreshPageAfterProductAddition: true });
  }
  return changed;
}

async function addShipProVariant({
  selectedVariant,
  variants,
  cartDiscountText,
}) {
  const cart = await fetchCart();
  const mrCart = getMRCartFromShopifyCart(cart);

  // filter the list to remove the selected variant, so that we can remove these from cart if present
  const filteredVariants = variants.filter(
    variant => variant.id !== selectedVariant.id,
  );

  // remove the filtered variants from cart
  const variantsToRemove = mrCart.filter(obj1 =>
    filteredVariants.some(obj2 => obj1.sp_variant_id === obj2.id),
  );

  const updates = {};
  variantsToRemove.forEach(elem => {
    const { key } = elem;
    updates[key] = 0;
  });

  let changed = false;
  if (Object.keys(updates).length > 0) {
    await updateCart({
      updates,
    });
    changed = true;
  }

  // check if selected variant is already in cart
  const variantInCart = mrCart.find(
    elem => elem.sp_variant_id === selectedVariant.id,
  );

  // if not then add it to cart
  if (!variantInCart) {
    const variantToAdd = {
      variant_id: selectedVariant.id,
      properties: getMonkDiscountProperties({
        isDiscountDisabled: true,
        off: '',
        offerType: 'ship_pro',
        properties: {},
        discountQuantity: 0,
        isDiscountQuantityRestricted: false,
        cartDiscountText,
      }),
    };
    await addOfferedVariantsToCart([variantToAdd]);
    changed = true;
  }
  return changed;
}

async function removeShipProVariant({ variants }) {
  // remove all the variants that are present in cart
  const cart = await fetchCart();
  const mrCart = getMRCartFromShopifyCart(cart);
  const variantsToRemove = mrCart.filter(obj1 =>
    variants.some(obj2 => obj1.sp_variant_id === obj2.id),
  );

  let changed = false;
  const updates = {};
  variantsToRemove.forEach(elem => {
    const { key } = elem;
    updates[key] = 0;
  });

  if (Object.keys(updates).length > 0) {
    await updateCart({
      updates,
    });
    changed = true;
  }
  return changed;
}

async function restrictShipProQuantities({
  mrCart,
  selectedVariant,
  variantIDs,
  isRefresh,
}) {
  let quantityNeeded = 1;
  const updates = {};
  for (let i = 0; i < mrCart.length; i += 1) {
    const { sp_variant_id, quantity, key } = mrCart[i];

    // restrict to 1 quantity for selected variant
    if (sp_variant_id === selectedVariant.id) {
      if (quantity >= quantityNeeded) {
        const updatedQuantity = quantityNeeded;
        quantityNeeded = 0;

        // restrict quantity to available quantity when price is 0 or flag is set
        if (quantity !== updatedQuantity) {
          updates[key] = updatedQuantity;
        }
      } else {
        quantityNeeded -= quantity;
      }
    }

    // remove from cart for non selected variants
    if (
      variantIDs.includes(sp_variant_id) &&
      selectedVariant.id !== sp_variant_id
    ) {
      updates[key] = 0;
    }
  }

  let cartChanged = false;
  if (Object.keys(updates).length > 0) {
    await updateCart({
      updates,
    });
    cartChanged = true;
  }

  if (cartChanged && isRefresh) {
    goToCart({ isPopup: false, isRefreshPageAfterProductAddition: true });
  }

  return cartChanged;
}
